import { menus } from '../lib/menus';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { SelectPanel } from './select-group';
import { ViewCount } from './view-count';
import { Pagination } from './pagination';

export const SearchBtns = ({ 
    currentMode, onToggle, isFilterOpen, onFilterToggle, toggleFilter, activeFilters, setActiveFilters, viewCount, setViewCount, 
    totalPages, currentPage, setCurrentPage
}) => {
    // Filter Screen for Mobile
    const handleFilterToggle = () => {
        onFilterToggle(isFilterOpen ? false : true);
    };

    // Switch mode table or card
    const isTable = currentMode === 'table';
    const handleViewModeToggle = () => {
        onToggle(isTable ? 'card' : 'table');
    };

    // Clear Filters
    const activeFilterEntries = Object.entries(activeFilters || {}).filter(([_, values]) => Array.isArray(values) && values.length > 0);
    const totalActiveCount = activeFilterEntries.reduce((sum, [_, values]) => sum + values.length, 0);

    const resetAllFilters = () => {
        const emptyFilters = Object.fromEntries(menus.map(m => [m.key, []]));
        setActiveFilters(emptyFilters);
    }

    return (
        <div className="flex flex-wrap focus-within:outline-none gap-2">

            {/* View Mode Toggle */}
            <button
                onClick={handleViewModeToggle}
                className="flex justify-center items-center bg-white rounded-2xl px-3 py-1.5 gap-2 hover:bg-gray-100 dark:hover:bg-zinc-600/30 dark:bg-zinc-700 dark:ring-zinc-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 active:scale-95 transition duration-150 ease-in-out"
                aria-label="Switch the result view type"
            >
                {isTable ? (
                    <>
                        <svg className="size-5 text-gray-600 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        <span className="text-gray-600 dark:text-gray-200 font-semibold mb-[1px] select-none">テーブル</span>
                    </>
                ) : (
                    <>
                        <svg className="size-5 text-gray-600 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        <span className="text-gray-600 dark:text-gray-200 font-semibold mb-[1px] select-none">カード</span>
                    </>
                )}
            </button>

            {/* Views Count */}
            <ViewCount viewCount={viewCount} setViewCount={setViewCount} />

            {/* Filter Panel on Mobile */}
            <button
                onClick={handleFilterToggle}
                className="lg:hidden flex justify-center items-center bg-white rounded-xl px-3 py-1.5 gap-2 hover:bg-gray-100 dark:hover:bg-zinc-600/30 dark:bg-zinc-700 dark:ring-zinc-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 active:scale-95 transition duration-150 ease-in-out"
                aria-label="Toggle Search Filter Panel"
            >
                <svg className="size-5 text-gray-600 dark:text-gray-200" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 00-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9" />
                </svg>
                <span className="text-gray-600 dark:text-gray-200 font-semibold truncate mb-[1px] select-none">フィルタ</span>
            </button>
            {/* Filter Dialog */}
            <Dialog open={isFilterOpen} as="div" className="relative z-10 focus:outline-none" onClose={handleFilterToggle}>
                <div className="fixed inset-0 z-20 w-screen overflow-y-auto bg-slate-200 dark:bg-zinc-900">
                    <div className="flex flex-col min-h-full items-center justify-start p-4 pb-0">
                        {/* Filter Panel */}
                        <DialogPanel 
                            transition
                            className="flex flex-col gap-3 w-full rounded-3xl bg-white dark:bg-zinc-800 duraiton-300 ease-out data-closed:transfrom-[scale(96%)] data-closed:opacity-0 overflow-auto"
                        >
                            <div className="flex flex-row justify-between items-center px-6 py-4 border-b-[1px] border-gray-200 dark:border-gray-500">
                                <DialogTitle className="font-bold text-lg dark:text-slate-100">Filters</DialogTitle>
                                <button
                                    onClick={handleFilterToggle}
                                    className="inline-flex items-center rounded-full bg-white hover:bg-gray-100 dark:hover:bg-gray-600/30 dark:bg-zinc-600/90 p-1.5 border-[1px] border-gray-200 dark:border-gray-500 font-semibold dark:text-slate-100 shadow-md dark:shadow-none shadow-slate-200 focus:not-data-focus:outline-none data-focus:outline data-focus:outline-white data-hover:bg-gray-600 data-open:bg-gray-700 active:scale-90 transition duration-150 ease-in-out focus:outline-none"
                                    aria-label="Close the Filter Screen"
                                >
                                    <svg className="size-5" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414" clipRule="evenodd"></path>
                                    </svg>
                                </button>
                            </div>
                            <div className="grid grid-cols-1 bg-white dark:bg-zinc-800 gap-4 mb-2">
                                <SelectPanel isFilterOpen={isFilterOpen} toggleFilter={toggleFilter} activeFilters={activeFilters} />
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            {/* Clear Filters */}
            {totalActiveCount > 0 && (
                <button
                    onClick={resetAllFilters}
                    className="flex justify-center items-center bg-white rounded-2xl px-3 py-1.5 gap-2 hover:bg-gray-100 dark:hover:bg-zinc-600/30 dark:bg-zinc-700 dark:ring-zinc-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 active:scale-95 transition duration-150 ease-in-out"
                    aria-label="Clear all filters"
                >
                    <svg className="size-5 text-gray-600 dark:text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-gray-600 dark:text-gray-200 font-semibold mb-[1px] select-none">条件をクリア</span>
                </button>
            )}

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    pageCount={totalPages}
                />
            )}
        </div>
    );
};